import { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { SquarePen } from 'lucide-react';

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogClose,
  DialogFooter,
} from '@/components/ui/dialog';
import { PASSWORD_PATTERN } from 'src/utils/validation/validation-patterns';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from 'src/context/auth/use-auth';

const formSchema = z
  .object({
    password: z.string().min(8).regex(PASSWORD_PATTERN),
    newPassword: z.string().min(8).regex(PASSWORD_PATTERN),
    confirmPassword: z.string().min(8).regex(PASSWORD_PATTERN),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

const PasswordDialog = () => {
  const [open, setOpen] = useState(false);
  const { changePassword } = useAuth();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });

  const onSubmit = async ({ password, newPassword }: z.infer<typeof formSchema>) => {
    try {
      await changePassword(password, newPassword);
      toast.success('Password updated!', {
        description: 'Your login details have been updated.',
      });
      setOpen(false);
      form.reset();
    } catch (e: unknown) {
      if (e instanceof Error) {
        if (e.name === 'NotAuthorizedException') toast.error('Invalid password');
        else toast.error(e.message);
      }
    }
  };

  const onOpenChange = (open: boolean) => {
    setOpen(open);
    if (!open) form.reset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <SquarePen />
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] gap-6">
        <DialogHeader className="gap-1.5">
          <DialogTitle className="text-base font-bold">Change password</DialogTitle>
          <DialogDescription className="text-xs">Update your password to keep your account secure.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Current password</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" placeholder="Enter your current password" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex justify-between gap-2">
                    New password
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="underline text-muted-foreground font-normal">Password policy</span>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        align="center"
                        className="bg-popover text-popover-foreground max-w-[270px] border border-border text-sm"
                      >
                        Your password must be at least 8 characters long and contain at least 1 uppercase letter, 1
                        number, and 1 special character.
                      </TooltipContent>
                    </Tooltip>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} type="password" placeholder="Enter your new password" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem className="mb-6">
                  <FormLabel>Confirm new password</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" placeholder="Confirm your new password" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="sm:justify-between">
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button type="submit" disabled={!form.formState.isValid} isLoading={form.formState.isSubmitting}>
                Save changes
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default PasswordDialog;
